import * as React from 'react';

import { numberToPrettyString } from '@cian/components';
import { IRange } from '../../../../common/types';
import { formatPhone } from '../../../../utils/formatters';
import { PromoLabel } from '../../../promo_label/promo_label';
import { ICoordinates, IFinishInfo } from '../../../../../typings/api';
import { Room } from '../../../../store/filters/model';
import { IRoom } from '../../../../store/pik_data/gks';
import { shortenNumber } from '@cian/utils/lib/shared/shorten_number';
import { ERoom } from '../../../../repositories/monolith-python/v1/filter-pik-newbuildings';

const styles = require('./map_preview.css');

const mapRoomToText: { [key: string]: string } = {
  fourRoom: '4-комн.',
  oneRoom: '1-комн.',
  studio: 'студия',
  threeRoom: '3-комн.',
  twoRoom: '2-комн.',
  fiveRoom: '5-комн.',
};

interface IProps {
  title?: string;
  publicUrl?: string;
  imgUrl?: string;
  price?: IRange;
  finishInfo?: IFinishInfo;
  address?: string;
  coordinates?: ICoordinates;
  phones?: string[];
  rooms?: IRoom[];
  onClose?(): void;
}

export class MapPreview extends React.PureComponent<IProps, object> {
  public render() {
    const { props } = this;

    const content = (
      <div className={styles['content']}>
        {this.renderPhoto()}
        <div className={styles['info']}>
          {this.renderTitle()}
          {this.renderAddress()}
          {this.renderFinishInfo()}
          {this.renderMinPrice()}
          {this.renderRooms()}
          {this.renderPhones()}
        </div>
      </div>
    );

    if (props.publicUrl) {
      return (
        <a href={props.publicUrl} target="_blank" rel="noreferrer" className={styles['map-preview']}>
          {content}
        </a>
      );
    }

    return <div className={styles['map-preview']}>{content}</div>;
  }

  private renderPhoto = () => {
    const { imgUrl } = this.props;

    if (!imgUrl) {
      return null;
    }

    return <figure className={styles['photo']} style={{ backgroundImage: `url(${imgUrl})` }} />;
  };

  private renderTitle = () => {
    const { title } = this.props;

    if (!title) {
      return null;
    }

    return <h5 className={styles['title']}>{title}</h5>;
  };

  private renderAddress = () => {
    const { address } = this.props;

    if (!address) {
      return null;
    }

    return <p className={styles['address']}>{address}</p>;
  };

  private renderFinishInfo = () => {
    const { finishInfo } = this.props;

    if (!finishInfo) {
      return null;
    }

    return <p className={styles['finish-info']}>{finishInfo.label}</p>;
  };

  private renderMinPrice = () => {
    const { price } = this.props;

    if (!price || !price.min) {
      return null;
    }

    return <PromoLabel labelStyle={styles['min-price']}>от {numberToPrettyString(price.min)} ₽</PromoLabel>;
  };

  private renderPhones = () => {
    const { phones } = this.props;

    if (!phones || phones.length === 0) {
      return null;
    }

    return (
      <ul className={styles['phones-list']}>
        {phones.map(phone => (
          <li className={styles['phone']} key={phone}>
            <a href={`tel:${phone}`} className={styles['phone-link']}>
              {formatPhone(phone)}
            </a>
          </li>
        ))}
      </ul>
    );
  };

  private getRoomText = (roomType: Room) => mapRoomToText[roomType as string];

  private renderRooms = () => {
    const { rooms } = this.props;

    if (!rooms || rooms.length === 0) {
      return null;
    }

    const roomsWithFirstStudio = [rooms[rooms.length - 1], ...rooms.slice(0, -1)];

    return (
      <div className={styles['rooms']}>
        {roomsWithFirstStudio.map(room => {
          const minPrice = shortenNumber(room.minPrice).replace('.', ',');
          const noData = Number(room.minPrice) === 0;
          const minPriceLabel = noData ? 'нет данных' : `от ${minPrice} руб.`;

          if (room.type === ERoom.FiveRoom && noData) {
            return null; // Пятикомнатных квартир очень мало. Показываем строчку только если они есть в продаже
          }

          return (
            <div key={room.type} className={styles['room']}>
              <div className={styles['count']}>{this.getRoomText(room.type)}</div>
              <div className={styles['line']} />
              <div className={styles['price']}>{minPriceLabel}</div>
            </div>
          );
        })}
      </div>
    );
  };
}
